import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, ExternalLink, MapPin, Phone, ScrollText } from "lucide-react";
import { Section } from "@/components/ui/section";
import { Container } from "@/components/ui/container";
import { BrandButton } from "@/components/ui/brand-button";
import Seo from "@/components/Seo";
import FAQAccordion from "@/components/FAQAccordion";
import QuickQuoteForm from "@/components/QuickQuoteForm";
import { usePhoneNumber } from "@/hooks/usePhoneNumber";
import { SITE_URL, SITE_NAME } from "@/lib/seo-constants";
import { breadcrumbListSchema, faqPageSchema } from "@/lib/jsonld";
import { LOCATION_BY_SLUG } from "@/data/locations";
import { SERVICES_DATA } from "@/data/services";
import { SERVICE_GUIDES } from "@/data/service-guides";
import {
    LOCAL_SERVICES,
    LOCAL_SERVICE_COPY,
    LOCAL_SERVICE_ROUTES,
    hasLocalService,
} from "@/data/location-services";

// /locations/:city/:service — e.g. /locations/brampton/fencing.
// Unique per page: the city's rules for this trade (verified, from
// data/locations.js), a hand-written local paragraph, and FAQs built from
// those rules. See data/location-services.js.

const buildFaqs = (loc, svc, rules) => {
    const find = (t) => rules.find((r) => r.title === t);
    const faqs = [];
    if (svc.slug === "fencing") {
        const f = find("Fence height");
        if (f) faqs.push({ q: `How tall can a fence be in ${loc.name}?`, a: f.body });
        const p = find("Pool enclosures");
        if (p) faqs.push({ q: `What are the pool fence rules in ${loc.name}?`, a: p.body });
    }
    if (svc.slug === "interlocking") {
        const d = find("Driveway widening") || find("Infill & driveways");
        if (d) faqs.push({ q: `Do I need a permit to widen my driveway in ${loc.name}?`, a: d.body });
    }
    if (svc.slug === "decking") {
        const d = find("Deck permits");
        if (d) faqs.push({ q: `Do I need a permit to build a deck in ${loc.name}?`, a: d.body });
        const t = find("Private trees") || find("Trees");
        if (t) faqs.push({ q: `Can trees affect where my deck goes in ${loc.name}?`, a: t.body });
    }
    faqs.push({
        q: `How do I get a ${svc.name.toLowerCase()} quote in ${loc.name}?`,
        a: `Send the form on this page or call 437-241-6287. We reply within one business day, visit your ${loc.name} property to measure and check the ${loc.municipality} rules for your lot, and give you a fixed written price.`,
    });
    return faqs;
};

const serviceSchema = (loc, svc, path) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    name: svc.h1(loc.name),
    serviceType: svc.name,
    url: `${SITE_URL}${path}`,
    description: svc.metaDescription(loc.name),
    provider: {
        "@type": "GeneralContractor",
        "@id": `${SITE_URL}/#localbusiness`,
        name: SITE_NAME,
        url: SITE_URL,
    },
    areaServed: {
        "@type": "City",
        name: loc.name,
        containedInPlace: { "@type": "AdministrativeArea", name: loc.region },
    },
});

const Hero = ({ loc, svc, copy }) => {
    const { phone, phoneHref } = usePhoneNumber();
    return (
        <section className="relative w-full bg-brand-dark text-white" data-testid="locsvc-hero">
            <Container className="py-16 md:py-20">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                    <div className="lg:col-span-7">
                        <nav className="text-xs text-white/50 mb-6" aria-label="Breadcrumb">
                            <Link to="/service-area" className="hover:text-brand-cyan">Service Area</Link>
                            <span className="mx-2">/</span>
                            <Link to={`/locations/${loc.slug}`} className="hover:text-brand-cyan">{loc.name}</Link>
                            <span className="mx-2">/</span>
                            <span className="text-white/80">{svc.name}</span>
                        </nav>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-wide text-white">
                            <span className="block font-sans text-xs uppercase tracking-[0.3em] text-brand-cyan font-semibold leading-normal mb-4">
                                {svc.eyebrow(loc.name)} · Reformity Inc.
                            </span>
                            {svc.h1(loc.name)}
                        </h1>
                        <p className="mt-6 max-w-2xl text-base md:text-lg text-white/80 font-light leading-relaxed">
                            {copy}
                        </p>
                        <div className="mt-8">
                            <BrandButton asChild variant="outline" tone="onDark" size="lg">
                                <a href={phoneHref} data-testid="locsvc-cta-call">
                                    <Phone className="h-4 w-4" strokeWidth={2.25} />
                                    Call {phone}
                                </a>
                            </BrandButton>
                        </div>
                    </div>
                    <div className="lg:col-span-5" data-testid="locsvc-quickquote">
                        <QuickQuoteForm
                            track="residential"
                            serviceSlug={svc.slug}
                            serviceName={svc.name}
                            defaultCity={loc.name}
                            source={`location_service_${loc.slug}_${svc.slug}`}
                            title={`Get a ${loc.name} ${svc.name.toLowerCase()} quote.`}
                        />
                    </div>
                </div>
            </Container>
        </section>
    );
};

const Rules = ({ loc, svc, rules }) => (
    <Section bg="white" data-testid="locsvc-rules">
        <Container>
            <div className="max-w-2xl mb-10">
                <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-3">
                    {loc.municipality} rules
                </p>
                <h2 className="font-display text-4xl sm:text-5xl tracking-wide text-brand-dark leading-none">
                    {svc.name} rules in {loc.name}.
                </h2>
                <p className="mt-4 text-base text-brand-dark/70 leading-relaxed">
                    Checked against the {loc.municipality}&apos;s own website. Lots differ, so we
                    confirm the rules for your address at the quote visit.
                </p>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {rules.map((r) => (
                    <li key={r.title} className="bg-brand-offwhite border border-brand-dark/10 p-6">
                        <div className="flex items-start gap-3">
                            <ScrollText className="h-5 w-5 mt-0.5 shrink-0 text-brand-cyan" strokeWidth={2.25} />
                            <div>
                                <h3 className="font-sans font-semibold text-lg text-brand-dark">{r.title}</h3>
                                <p className="mt-2 text-sm text-brand-dark/75 leading-relaxed">{r.body}</p>
                                {r.source && !r.source.startsWith("/") && (
                                    <a
                                        href={r.source}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-cyan hover:text-brand-coral"
                                    >
                                        {r.sourceLabel || "Official source"}
                                        <ExternalLink className="h-3.5 w-3.5" strokeWidth={2.25} />
                                    </a>
                                )}
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        </Container>
    </Section>
);

const WhatWeBuild = ({ loc, svc, data }) => (
    <Section dark data-testid="locsvc-materials">
        <Container>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-5">
                    <p className="text-xs uppercase tracking-[0.25em] text-brand-cyan font-semibold mb-3">
                        What we build in {loc.name}
                    </p>
                    <h2 className="font-display text-4xl sm:text-5xl tracking-wide text-white leading-none">
                        {data.heroTitle}
                    </h2>
                    <Link
                        to={`/services/${svc.slug}`}
                        className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-cyan hover:underline"
                    >
                        Full {svc.name.toLowerCase()} details, materials &amp; process
                        <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                    </Link>
                </div>
                {/* Names only — the full descriptions live on the service page;
                    repeating them on every city page made these near-duplicates. */}
                <ul className="lg:col-span-7 flex flex-wrap content-start gap-2">
                    {(data.materials || []).map((m) => (
                        <li key={m.name} className="border border-white/15 px-4 py-2.5 text-sm font-semibold text-white">
                            {m.name}
                        </li>
                    ))}
                </ul>
            </div>
        </Container>
    </Section>
);

const Guides = ({ guides }) =>
    guides.length === 0 ? null : (
        <Section data-testid="locsvc-guides">
            <Container>
                <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-3">Guides</p>
                <h2 className="font-display text-4xl tracking-wide text-brand-dark leading-none mb-8">
                    Costs, permits &amp; how-tos.
                </h2>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-px bg-brand-dark/10">
                    {guides.map((g) => (
                        <li key={g.path} className="bg-white">
                            <Link to={g.path} className="group flex items-start justify-between gap-4 p-6 h-full hover:bg-brand-offwhite">
                                <span className="font-semibold text-brand-dark group-hover:text-brand-cyan leading-snug">{g.title}</span>
                                <ArrowRight className="h-5 w-5 mt-0.5 shrink-0 text-brand-dark/40 group-hover:text-brand-cyan" strokeWidth={2.25} />
                            </Link>
                        </li>
                    ))}
                </ul>
            </Container>
        </Section>
    );

const Related = ({ loc, svc }) => {
    const otherServices = Object.values(LOCAL_SERVICES).filter(
        (s) => s.slug !== svc.slug && hasLocalService(loc.slug, s.slug),
    );
    const otherCities = LOCAL_SERVICE_ROUTES.filter((r) => r.service === svc.slug && r.city !== loc.slug);
    return (
        <Section dark className="py-14 md:py-16" data-testid="locsvc-related">
            <Container>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div>
                        <p className="text-xs uppercase tracking-[0.25em] text-brand-cyan font-semibold mb-4">
                            More in {loc.name}
                        </p>
                        <ul className="flex flex-wrap gap-2">
                            {otherServices.map((s) => (
                                <li key={s.slug}>
                                    <Link to={`/locations/${loc.slug}/${s.slug}`} className="inline-flex border border-white/15 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-white/80 hover:bg-brand-coral hover:border-brand-coral hover:text-white">
                                        {s.name} in {loc.name}
                                    </Link>
                                </li>
                            ))}
                            <li>
                                <Link to={`/locations/${loc.slug}`} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-brand-cyan hover:underline">
                                    All {loc.name} services <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} />
                                </Link>
                            </li>
                        </ul>
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-[0.25em] text-brand-cyan font-semibold mb-4">
                            {svc.name} in other cities
                        </p>
                        <ul className="flex flex-wrap gap-2">
                            {otherCities.map((r) => (
                                <li key={r.path}>
                                    <Link to={r.path} className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-white/80 hover:bg-brand-coral hover:border-brand-coral hover:text-white">
                                        <MapPin className="h-3.5 w-3.5" strokeWidth={2.5} />
                                        {LOCATION_BY_SLUG[r.city].name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </Container>
        </Section>
    );
};

export default function LocationService() {
    const { city, service } = useParams();
    const loc = LOCATION_BY_SLUG[city];
    const svc = LOCAL_SERVICES[service];
    if (!loc) return <Navigate to="/service-area" replace />;
    if (!svc || !hasLocalService(city, service)) return <Navigate to={`/locations/${city}`} replace />;

    const path = `/locations/${loc.slug}/${svc.slug}`;
    const copy = LOCAL_SERVICE_COPY[loc.slug][svc.slug];
    const rules = loc.rules.filter((r) => svc.ruleTopics.includes(r.title));
    const faqs = buildFaqs(loc, svc, rules);
    const data = SERVICES_DATA[svc.slug];
    const guides = (SERVICE_GUIDES[`residential:${svc.slug}`] || []).slice(0, 4);

    return (
        <>
            <Seo
                title={svc.metaTitle(loc.name)}
                description={svc.metaDescription(loc.name)}
                path={path}
                jsonLd={[
                    serviceSchema(loc, svc, path),
                    faqPageSchema(faqs),
                    breadcrumbListSchema([
                        { name: "Service Area", path: "/service-area" },
                        { name: loc.name, path: `/locations/${loc.slug}` },
                        { name: svc.name, path },
                    ]),
                ]}
            />
            <Hero loc={loc} svc={svc} copy={copy} />
            <Rules loc={loc} svc={svc} rules={rules} />
            <WhatWeBuild loc={loc} svc={svc} data={data} />
            <Guides guides={guides} />
            <Section bg="white" data-testid="locsvc-faq">
                <Container size="narrow">
                    <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-3">
                        {loc.name} {svc.name} FAQ
                    </p>
                    <h2 className="font-display text-4xl sm:text-5xl tracking-wide text-brand-dark leading-none mb-8">
                        Quick answers.
                    </h2>
                    <FAQAccordion items={faqs} testidPrefix={`locsvc-faq-${loc.slug}-${svc.slug}`} />
                </Container>
            </Section>
            <Related loc={loc} svc={svc} />
        </>
    );
}
